import { parseAthenaRightKey, type AthenaRightKey } from "../../rights/key.ts";
import {
	AUTHORIZATION_PLATFORM_READ,
	AUTHORIZATION_PLATFORM_WRITE,
	AUTHORIZATION_ROLES_READ,
	AUTHORIZATION_ROLES_WRITE,
	ORGANIZATION_MEMBERS_INVITE,
	ORGANIZATION_MEMBERS_READ,
	ORGANIZATION_MEMBERS_WRITE,
	ORGANIZATION_OWNERS_ASSIGN,
	ORGANIZATION_LIFECYCLE_DELETE,
	listAthenaAuthorizationRights,
} from "./catalog.ts";
import type { AthenaAuthorizationRoleRecord } from "./types.ts";

export const PLATFORM_ADMIN_ROLE = "platform_admin";
export const PLATFORM_CUSTOMER_ROLE = "platform_customer";
export const PLATFORM_UNAUTHORIZED_ROLE = "platform_unauthorized";
export const ORGANIZATION_OWNER_ROLE = "organization_owner";
export const ORGANIZATION_ADMIN_ROLE = "organization_admin";
export const ORGANIZATION_MEMBER_ROLE = "organization_member";

export interface BuiltInAuthorizationRoleTemplate {
	assignable: boolean;
	description: string;
	id: string;
	key: string;
	name: string;
	protected: boolean;
	rights: readonly AthenaRightKey[];
	scopeKind: "platform" | "organization";
	systemKind: "owner" | "admin" | "member" | null;
}

function billingKeysMatching(
	predicate: (key: string) => boolean,
): AthenaRightKey[] {
	return listAthenaAuthorizationRights()
		.filter((entry) => entry.domain === "billing" && predicate(entry.key))
		.map((entry) => entry.key);
}

function storageKeys(): AthenaRightKey[] {
	return listAthenaAuthorizationRights()
		.filter((entry) => entry.domain === "storage")
		.map((entry) => entry.key);
}

const CUSTOMER_BILLING: readonly AthenaRightKey[] = Object.freeze([
	parseAthenaRightKey("billing.catalog.read"),
	parseAthenaRightKey("billing.self.invoices.read"),
	parseAthenaRightKey("billing.self.payments.read"),
	parseAthenaRightKey("billing.self.subscription.read"),
	parseAthenaRightKey("billing.self.subscription.write"),
	parseAthenaRightKey("billing.self.checkout.write"),
	parseAthenaRightKey("billing.customers.write"),
]);

export const BUILTIN_AUTHORIZATION_ROLES: readonly BuiltInAuthorizationRoleTemplate[] =
	Object.freeze([
		{
			assignable: true,
			description: "Platform operator",
			id: PLATFORM_ADMIN_ROLE,
			key: PLATFORM_ADMIN_ROLE,
			name: "Platform administrator",
			protected: true,
			rights: Object.freeze([
				...billingKeysMatching(() => true),
				...storageKeys(),
				AUTHORIZATION_PLATFORM_READ,
				AUTHORIZATION_PLATFORM_WRITE,
				AUTHORIZATION_ROLES_READ,
				ORGANIZATION_MEMBERS_READ,
			]),
			scopeKind: "platform",
			systemKind: "admin",
		},
		{
			assignable: true,
			description: "Customer account",
			id: PLATFORM_CUSTOMER_ROLE,
			key: PLATFORM_CUSTOMER_ROLE,
			name: "Customer",
			protected: true,
			rights: Object.freeze([
				...CUSTOMER_BILLING,
				...storageKeys().filter((key) => key !== "storage.put" && key !== "storage.delete"),
			]),
			scopeKind: "platform",
			systemKind: "member",
		},
		{
			assignable: true,
			description: "No rights",
			id: PLATFORM_UNAUTHORIZED_ROLE,
			key: PLATFORM_UNAUTHORIZED_ROLE,
			name: "Unauthorized",
			protected: true,
			rights: Object.freeze([]),
			scopeKind: "platform",
			systemKind: null,
		},
		{
			assignable: true,
			description: "Organization owner",
			id: ORGANIZATION_OWNER_ROLE,
			key: ORGANIZATION_OWNER_ROLE,
			name: "Organization owner",
			protected: true,
			rights: Object.freeze([
				ORGANIZATION_MEMBERS_READ,
				ORGANIZATION_MEMBERS_WRITE,
				ORGANIZATION_MEMBERS_INVITE,
				ORGANIZATION_OWNERS_ASSIGN,
				ORGANIZATION_LIFECYCLE_DELETE,
				AUTHORIZATION_ROLES_READ,
				AUTHORIZATION_ROLES_WRITE,
			]),
			scopeKind: "organization",
			systemKind: "owner",
		},
		{
			assignable: true,
			description: "Organization administrator",
			id: ORGANIZATION_ADMIN_ROLE,
			key: ORGANIZATION_ADMIN_ROLE,
			name: "Organization administrator",
			protected: true,
			rights: Object.freeze([
				ORGANIZATION_MEMBERS_READ,
				ORGANIZATION_MEMBERS_WRITE,
				ORGANIZATION_MEMBERS_INVITE,
				AUTHORIZATION_ROLES_READ,
			]),
			scopeKind: "organization",
			systemKind: "admin",
		},
		{
			assignable: true,
			description: "Organization member",
			id: ORGANIZATION_MEMBER_ROLE,
			key: ORGANIZATION_MEMBER_ROLE,
			name: "Organization member",
			protected: true,
			rights: Object.freeze([ORGANIZATION_MEMBERS_READ]),
			scopeKind: "organization",
			systemKind: "member",
		},
	]);

export function mapLegacyUserRole(role: string | null | undefined): string {
	const trimmed = role?.trim() ?? "";
	if (trimmed === "admin") {
		return PLATFORM_ADMIN_ROLE;
	}
	if (trimmed === "unauthorized") {
		return PLATFORM_UNAUTHORIZED_ROLE;
	}
	if (trimmed === "" || trimmed === "customer") {
		return PLATFORM_CUSTOMER_ROLE;
	}
	return PLATFORM_UNAUTHORIZED_ROLE;
}

export function mapLegacyMemberRole(role: string | null | undefined): string {
	const trimmed = role?.trim().toLowerCase() ?? "member";
	if (trimmed === "owner") {
		return ORGANIZATION_OWNER_ROLE;
	}
	if (trimmed === "admin") {
		return ORGANIZATION_ADMIN_ROLE;
	}
	if (trimmed === "member") {
		return ORGANIZATION_MEMBER_ROLE;
	}
	return ORGANIZATION_MEMBER_ROLE;
}

export function resolveMemberAssignmentRole(
	role: string | null | undefined,
): string {
	const trimmed = role?.trim() ?? "member";
	const lower = trimmed.toLowerCase();
	if (lower === "owner" || lower === ORGANIZATION_OWNER_ROLE) {
		return ORGANIZATION_OWNER_ROLE;
	}
	if (lower === "admin" || lower === ORGANIZATION_ADMIN_ROLE) {
		return ORGANIZATION_ADMIN_ROLE;
	}
	if (lower === "member" || lower === ORGANIZATION_MEMBER_ROLE) {
		return ORGANIZATION_MEMBER_ROLE;
	}
	return trimmed;
}

export function persistedMemberRole(
	role: Pick<AthenaAuthorizationRoleRecord, "key" | "systemKind">,
): string {
	if (role.systemKind === "owner") {
		return "owner";
	}
	if (role.systemKind === "admin") {
		return "admin";
	}
	if (role.systemKind === "member") {
		return "member";
	}
	return role.key;
}

export function grantRankRole(
	role: Pick<AthenaAuthorizationRoleRecord, "key" | "systemKind">,
): string {
	if (role.systemKind === "owner" || role.key === ORGANIZATION_OWNER_ROLE) {
		return "owner";
	}
	if (role.systemKind === "admin" || role.key === ORGANIZATION_ADMIN_ROLE) {
		return "admin";
	}
	if (role.systemKind === "member" || role.key === ORGANIZATION_MEMBER_ROLE) {
		return "member";
	}
	return "admin";
}

export function builtinRoleRecord(
	template: BuiltInAuthorizationRoleTemplate,
): AthenaAuthorizationRoleRecord {
	return {
		assignable: template.assignable,
		id: template.id,
		key: template.key,
		name: template.name,
		organizationId: null,
		protected: template.protected,
		scopeKind: template.scopeKind,
		systemKind: template.systemKind,
		version: 1,
	};
}

export function compatibilityMemberRole(roleKey: string): string {
	if (roleKey === ORGANIZATION_OWNER_ROLE) {
		return "owner";
	}
	if (roleKey === ORGANIZATION_ADMIN_ROLE) {
		return "admin";
	}
	return "member";
}

export function compatibilityUserRole(roleKey: string): string | null {
	if (roleKey === PLATFORM_ADMIN_ROLE) {
		return "admin";
	}
	if (roleKey === PLATFORM_UNAUTHORIZED_ROLE) {
		return "unauthorized";
	}
	if (roleKey === PLATFORM_CUSTOMER_ROLE) {
		return "customer";
	}
	return null;
}
